/**
 * BRA-61 — Modal for exporting a Brain consulta (chat session) to PDF.
 * Renders the filter form and delegates the request to `useConsultaPdfExport`.
 * Kept in sync with ExportMarkersDialog's visual conventions so both exports
 * feel like the same feature family.
 */

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useConsultaPdfExport, type ConsultaExportFilters } from "../hooks/useConsultaPdfExport";

type SourceType = "log" | "trans" | "otf";

interface ConsultaPdfExportModalProps {
  open: boolean;
  sessionId: string | null;
  onClose: () => void;
}

export const ConsultaPdfExportModal: React.FC<ConsultaPdfExportModalProps> = ({
  open,
  sessionId,
  onClose,
}) => {
  const { t } = useTranslation();
  const { exportSession, exporting, error } = useConsultaPdfExport(sessionId);

  const [sources, setSources] = useState<Record<SourceType, boolean>>({
    log: true,
    trans: true,
    otf: true,
  });
  const [excludeUserMessages, setExcludeUserMessages] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSources({ log: true, trans: true, otf: true });
    setExcludeUserMessages(false);
  }, [open]);

  const handleDownload = async () => {
    if (!sessionId) return;
    // Only forward sourceTypes when the user has actually narrowed them;
    // otherwise the server treats "all three selected" as "no filter" too,
    // but sending the list anyway would break the "all sources" semantics
    // if someone later extends the enum server-side.
    const allSources = sources.log && sources.trans && sources.otf;
    const sourceTypes = allSources
      ? undefined
      : (Object.keys(sources).filter((k) => sources[k as SourceType]) as SourceType[]);

    const filters: ConsultaExportFilters = {
      sourceTypes,
      excludeUserMessages: excludeUserMessages || undefined,
    };

    const ok = await exportSession(filters);
    if (ok) onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !exporting) onClose();
      }}
    >
      <DialogContent className="sm:max-w-[520px]" data-testid="consulta-pdf-export-dialog">
        <DialogHeader>
          <DialogTitle className="text-[var(--color-accent-warning)] text-lg font-black uppercase tracking-wider">
            {t("consultaPdf.modal.title", "Exportar consulta a PDF")}
          </DialogTitle>
          <DialogDescription className="text-[#ccc] text-[0.95rem] leading-relaxed">
            {t("consultaPdf.modal.subtitle", "Elige qué incluir en el PDF descargado.")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-2">
          <div className="flex flex-col gap-2">
            <span className="text-sm text-[#ccc]">
              {t("consultaPdf.modal.sourceTypes", "Tipos de fuente")}
            </span>
            <div className="flex flex-wrap gap-4">
              {(
                [
                  ["log", t("consultaPdf.modal.sourceLog", "Logs")],
                  ["trans", t("consultaPdf.modal.sourceTrans", "Transcripciones")],
                  ["otf", t("consultaPdf.modal.sourceOtf", "OTFs")],
                ] as [SourceType, string][]
              ).map(([k, label]) => (
                <label
                  key={k}
                  className="flex items-center gap-2 text-sm text-[#ccc] cursor-pointer"
                >
                  <Checkbox
                    checked={sources[k]}
                    onCheckedChange={(v) => setSources((s) => ({ ...s, [k]: v === true }))}
                    data-testid={`consulta-pdf-src-${k}`}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm text-[#ccc] cursor-pointer">
            <Checkbox
              checked={excludeUserMessages}
              onCheckedChange={(v) => setExcludeUserMessages(v === true)}
              data-testid="consulta-pdf-exclude-user"
            />
            {t(
              "consultaPdf.modal.excludeUserMessages",
              "Excluir mis mensajes (solo respuestas del Brain)",
            )}
          </label>

          {error && (
            <div
              className="text-sm text-[var(--color-accent-danger,#ff5555)]"
              data-testid="consulta-pdf-error"
            >
              {t("consultaPdf.modal.downloadFailed", "No se pudo generar el PDF.")} ({error})
            </div>
          )}
        </div>

        <DialogFooter className="bg-transparent border-t-0 mx-0 mb-0 p-0 flex-row justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={exporting}>
            {t("consultaPdf.modal.cancel", "Cancelar")}
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!sessionId || exporting}
            data-testid="consulta-pdf-download"
            className="bg-[var(--color-accent-success)] text-black hover:bg-[var(--color-accent-success)]/90"
          >
            {exporting
              ? t("consultaPdf.modal.generating", "Generando...")
              : t("consultaPdf.modal.download", "Descargar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConsultaPdfExportModal;
